import axios from 'axios';
import { AUTH } from 'endpointsConst';
import { getCookie } from './cookies';
import { showErrorMessage } from '../helpers/notifications';
import i18n from 'i18n';
import { refreshAuthTokens } from 'utils/refreshAuthorizationToken';
import { forceLogout } from './forceLogout';

let requestCount = 0;
let interceptorId = null;
let wait = false;
let refreshPromise = null;

export default function setAuthorizationToken() {
  const accessToken = getCookie('access_token');
  const refreshToken = getCookie('refresh_token');

  // Устанавливаем interceptor, если есть refresh_token, даже если нет access_token
  // Это позволяет автоматически получить новый access_token при первом запросе
  if (accessToken || refreshToken) {
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      // Если нет access_token, удаляем заголовок (он может быть устаревшим)
      delete axios.defaults.headers.common['Authorization'];
    }

    if (interceptorId !== null) {
      axios.interceptors.response.eject(interceptorId);
    }

    interceptorId = axios.interceptors.response.use(
      function (response) {
        return response;
      },
      async function (error) {
        const originalRequest = error?.config;

        // Если нет originalRequest, это может быть ошибка сети или отмененный запрос
        if (!originalRequest) {
          // Если это 401 без config, возможно токен уже невалиден
          if (error?.response?.status === 401) {
            const currentRefreshToken = getCookie('refresh_token');
            if (currentRefreshToken && !wait && requestCount < 2) {
              // console.warn('[Auth] 401 error without request config, attempting refresh');
              try {
                await refreshAuthTokens(currentRefreshToken);

                axios.defaults.headers.common['Authorization'] = 'Bearer ' + getCookie('access_token');
                // Не можем повторить запрос без originalRequest, но обновили токен
                return Promise.reject(error);
              } catch (refreshError) {
                // console.error('[Auth] Refresh token failed (no config):', refreshError);
                forceLogout();
                return Promise.reject(refreshError);
              }
            }
          }
          return Promise.reject(error);
        }

        const requestUrl = originalRequest.url || (originalRequest.baseURL ? originalRequest.baseURL + originalRequest.url : '');

        // Пропускаем запросы на refresh, чтобы избежать бесконечного цикла
        const isRefreshRequest =
          requestUrl?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url === AUTH.REFRESH;

        if (isRefreshRequest) {
          return Promise.reject(error);
        }

        // Обрабатываем 401 ошибки
        if (error?.response?.status === 401) {
          // Если уже были попытки или запрос уже был повторен
          if (originalRequest._retry || requestCount >= 2) {
            // console.error('[Auth] 401 after max retries, forcing logout');
            requestCount = 0;
            forceLogout();
            return Promise.reject(error);
          }

          // Если уже идет обновление токена, ждем его завершения
          if (refreshPromise) {
            try {
              await refreshPromise;
              const newAccessToken = getCookie('access_token');
              if (newAccessToken) {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
              }
              return axios(originalRequest);
            } catch (refreshError) {
              forceLogout();
              return Promise.reject(refreshError);
            }
          }

          const currentRefreshToken = getCookie('refresh_token');

          if (!currentRefreshToken) {
            // console.warn('[Auth] 401 error but no refresh token available');
            forceLogout();
            return Promise.reject(error);
          }

          // Создаем Promise для обновления токена, чтобы другие запросы могли его ждать
          originalRequest._retry = true;
          requestCount += 1;
          wait = true;

          refreshPromise = (async () => {
            try {
              // console.log('[Auth] Attempting to refresh token...');
              const res = await refreshAuthTokens(currentRefreshToken);

              axios.defaults.headers.common['Authorization'] = 'Bearer ' + getCookie('access_token');

              // console.log('[Auth] Token refreshed successfully');
              return res;
            } catch (refreshError) {
              console.error('[Auth] Refresh token failed:', refreshError);

              if (refreshError?.response?.status === 401 && refreshError?.response?.data?.detail === 'errors.authOtherDevice') {
                const errorMessage = i18n.t(refreshError?.response?.data?.detail);
                showErrorMessage(errorMessage);
                setTimeout(() => {
                  forceLogout();
                }, 3000);
              } else {
                forceLogout();
              }

              throw refreshError;
            } finally {
              requestCount = 0;
              wait = false;
              refreshPromise = null;
            }
          })();

          try {
            await refreshPromise;
            // Убеждаемся, что headers существуют
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = 'Bearer ' + getCookie('access_token');
            // console.log('[Auth] Retrying original request');
            return axios(originalRequest);
          } catch (refreshError) {
            forceLogout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  } else {
    // Нет ни access_token, ни refresh_token - удаляем interceptor
    delete axios.defaults.headers.common['Authorization'];
    if (interceptorId !== null) {
      axios.interceptors.response.eject(interceptorId);
      interceptorId = null;
    }
  }
}
